import type IAutoRunAddinCommand from './interfaces/IAutoRunAddinCommand';
import type LaunchEventType from 'owa-service/lib/contract/LaunchEventType';
import type Extension from 'owa-service/lib/contract/Extension';
import { ExtensibilityModeEnum } from 'owa-addins-types';
import type Control from 'owa-service/lib/contract/Control';
import ExtensionPointCollection from 'owa-service/lib/contract/ExtensionPointCollection';
import type RequestedCapabilities from 'owa-service/lib/contract/RequestedCapabilities';
import type ConsentStateType from 'owa-service/lib/contract/ConsentStateType';
import type LicenseType from 'owa-service/lib/contract/LicenseType';

/**
 *
 * @class AutoRunAddinCommand
 * @implements {IAutoRunAddinCommand}
 */

class AutoRunAddinCommand implements IAutoRunAddinCommand {
    constructor(
        public event: LaunchEventType,
        public control: Control,
        protected extension: Extension,
        public mode: ExtensibilityModeEnum
    ) {}

    /**
     * Returns the launchEventType for the addin.
     */
    getLaunchEventType(): LaunchEventType {
        return this.event;
    }

    /**
     * Gets Control for the addin.
     */
    getControl(): Control {
        return this.control;
    }

    /**
     * (Gets the Addin Command's EntryPoint)
     *
     * @returns {string} (The entrypoint Url)
     */
    get_EntryPoint(): string {
        switch (this.mode) {
            case ExtensibilityModeEnum.LaunchEvent:
                return this.extension?.ExtensionPointCollection.LaunchEvent.SourceLocation;
            default:
                return null;
        }
    }

    /**
     * Gets IconUrl for the addin.
     */
    get_IconUrl?(): string {
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (231,9): Type 'string | undefined' is not assignable to type 'string'.
        // @ts-ignore
        return this.extension?.IconUrl;
    }

    /**
     * Gets the label for the addin.
     *
     * @returns {string} (The label of the addin command)
     */
    get_Label(): string {
        return this.control?.Label;
    }

    /**
     * Gets a uniqueId for the addin.
     *
     * @returns {string} (A unique Id for the addin)
     */
    get_Id(): string {
        return this.extension?.Id;
    }

    /**
     * Gets OriginString for the addin.
     */
    get_OriginString(): string {
        return this.extension?.OriginString;
    }

    /**
     * Gets Product Id for the addin.
     */
    get_ExtensionId?(): string {
        return this.extension?.Id;
    }

    /**
     * Gets MarketPlaceAssetId for the addin.
     */
    get_MarketPlaceAssetId?(): string {
        return this.extension?.MarketplaceAssetID;
    }

    /**
     * Gets TypeString for the addin.
     */
    get_TypeString?(): string {
        return this.extension?.TypeString;
    }

    /**
     * Gets ProviderName for the addin.
     */
    get_ProviderName?(): string {
        return this.extension?.ProviderName;
    }

    /**
     * Gets ContentMetaData for the addin.
     */
    get_ConsentMetaData?(): string {
        return this.extension?.ConsentMetadata;
    }

    /**
     * Gets MarketplaceContentMarket for the addin.
     */
    get_MarketPlaceContentMarket?(): string {
        return this.extension?.MarketplaceContentMarket;
    }

    /**
     * Gets WebApplicationResource for the addin.
     */
    get_WebApplicationResource?(): string {
        return this.extension?.WebApplicationResource;
    }

    /**
     * Gets ExtensionPointCollection for the addin.
     */
    get_ExtensionPointCollection?(): ExtensionPointCollection {
        return this.extension?.ExtensionPointCollection;
    }

    /**
     * Gets Extension DisplayName for addin.
     *
     * @returns {string} (Extension DisplayName for the addin)
     */
    get_ExtensionDisplayName?(): string {
        return this.extension?.DisplayName;
    }

    /**
     * Gets the size 16px Icon for the addin.
     */
    get_Size16Icon(): string {
        if (this.control && this.control.Icon) {
            return this.control.Icon.Size16Url;
        }
        return null;
    }

    /**
     * Gets ExtensionPointCollection for the addin.
     */
    get_ExtendedPermissions?(): string[] {
        return this.extension?.ExtendedPermissions;
    }

    /**
     * Gets AppStatus for the addin.
     */
    get_AppStatus?(): string {
        return this.extension?.AppStatus;
    }

    /**
     * Gets LicenseType for the addin.
     */
    get_LicenseType?(): LicenseType {
        return this.extension?.LicenseType;
    }

    /**
     * Gets Settings for the addin.
     */
    get_Settings?(): string {
        return this.extension?.Settings;
    }

    /**
     * Gets Version for the addin.
     */
    get_Version?(): string {
        return this.extension?.Version;
    }

    /**
     * Gets appDomains for the addin.
     */
    get_AppDomains?(): string[] {
        return this.extension?.AppDomains;
    }

    /**
     * Gets Type for the addin.
     */
    get_Type?(): string {
        return this.extension?.Type;
    }

    /**
     * Gets ConsentSate for the addin.
     */
    get_ConsentState?(): ConsentStateType {
        return this.extension?.ConsentState;
    }

    /**
     * Gets RequestedCapbilities for the addin.
     */
    get_RequestedCapabilities?(): RequestedCapabilities {
        return this.extension?.RequestedCapabilities;
    }

    /**
     * Gets EndNodeUrl for the addin.
     */
    get_EndNodeUrl?(): string {
        return this.extension?.EndNodeUrl;
    }

    /**
     * Sets ConsentState for the addin.
     */
    set_ConsentState(consentState: ConsentStateType) {
        this.extension.ConsentState = consentState;
    }

    /**
     * Sets RequestedCapabilities for the addin.
     */
    set_RequestedCapabilities(requestedCapabilities: RequestedCapabilities) {
        this.extension.RequestedCapabilities = requestedCapabilities;
    }

    /**
     * Sets TypeString for the addin.
     */
    set_TypeString(typeString: string) {
        this.extension.TypeString = typeString;
    }

    /**
     * Sets ExtensionId for the addin.
     */
    set_ExtensionId(id: string) {
        this.extension.Id = id;
    }

    /**
     * Sets OriginString for the addin.
     */
    set_OriginString(originString: string) {
        this.extension.OriginString = originString;
    }

    /**
     * Sets Settings for the addin.
     */
    set_Settings(settings: string) {
        this.extension.Settings = settings;
    }
}

export default AutoRunAddinCommand;
